<template lang="html">
  <div>
    <!-- DESKTOP -->
    <div
      :class="`hidden ${breakpoint}:block`"
      role="tablist"
      @keyup.right="right(childActive)"
      @keyup.left="left(childActive)"
    >
      <button
        v-for="(child, index) in children"
        :key="index"
        ref="li"
        :class="{ activeChild: childActive == index }"
        :aria-selected="childActive === index ? 'true' : 'false'"
        :aria-controls="`tabpanel-${uid}-${index}`"
        :tabindex="childActive !== index ? -1 : 0"
        :id="`tab-${uid}-${index}`"
        role="tab"
        v-bind="child.attrs"
        :style="styleAlignIcon(child.icon)"
        class="vs-tabs--btn"
        type="button"
        @click="activeChild(index)"
        v-on="child.listeners"
      >
        <vs-icon
          v-if="child.icon"
          :icon-pack="child.iconPack"
          :icon="child.icon"
          :color="color"
          class="vs-tabs--btn-icon"
        ></vs-icon>
        <span v-if="child.label">{{ child.label }}</span>
      </button>
    </div>
    <hr aria-hidden="true" />

    <!-- MOBILE -->
    <div :class="`${breakpoint}:hidden`">
      <button
        class="dropdown flex items-center justify-between cursor-pointer header"
        @click="expand = !expand"
        :aria-expanded="expand ? 'true' : 'false'"
      >
        {{ children.length > 0 ? children[childActive].label : 'Label' }}
        <chevron-up-icon v-if="expand" color="#000" />
        <chevron-down-icon v-else color="#000" />
      </button>
      <div v-if="expand" class="dropdown">
        <ul class="bg-white">
          <li
            v-for="(child, index) in children"
            ref="li"
            :class="{ activeChild: childActive == index }"
            :key="index"
          >
            <button @click="changeTab(index)" class="no-style">
              {{ child.label }}
            </button>
          </li>
        </ul>
      </div>
    </div>

    <div class="con-slot-tabs">
      <slot />
    </div>
  </div>
</template>

<script>
let tabsUid = 0;
import _color from '../assets/utils/color';
import { ChevronDownIcon, ChevronUpIcon } from 'vue-feather-icons';

export default {
  name: 'vs-tabs',
  components: {
    ChevronDownIcon,
    ChevronUpIcon,
  },
  props: {
    value: {
      default: 0,
      type: [Number, String],
    },
    color: {
      default: 'primary',
      type: String,
    },
    tagColor: {
      default: 'primary',
      type: String,
    },
    alignment: {
      default: 'left',
      type: String,
    },
    position: {
      default: 'top',
      type: String,
    },
    breakpoint: {
      default: 'lg',
      type: String,
    },
  },
  data: () => ({
    // Unique per <tabs> instance, so ids stay unique when a page has several.
    uid: (tabsUid += 1),
    topx: 'auto',
    heightx: 2,
    hover: false,
    children: [],
    childActive: 0,
    leftx: 0,
    widthx: 0,
    these: false,
    expand: false,
  }),
  computed: {
    styleTab() {
      return {
        color: _color.getColor(this.color, 1),
      };
    },
    stylex() {
      return {
        top: `${this.topx}px`,
        left: `${this.leftx}px`,
        width: `${this.widthx}px`,
        height: `${this.heightx}px`,
        background: `linear-gradient(30deg, ${_color.getColor(this.color, 1)} 0%, ${_color.getColor(
          this.color,
          0.5,
        )} 100%)`,
        boxShadow: `0px 0px 8px 0px ${_color.getColor(this.color, 0.5)}`,
        transform: `scaleX(${this.these ? 1.3 : 1})`,
      };
    },
  },
  watch: {
    value(index) {
      const activeIndex = this.parseIndex(index);
      this.activeChild(activeIndex);
    },
  },
  mounted() {
    const activeIndex = this.parseIndex(this.value);
    this.childActive = activeIndex;
    this.$nextTick(() => {
      this.activeChild(activeIndex, true);
    });
  },
  methods: {
    right(val) {
      const maxLength = this.$children.length - 1;

      if (val <= maxLength) {
        this.childActive = this.childActive + 1;
      }

      if (this.childActive > maxLength) {
        this.childActive = 0;
      }

      this.activeChild(this.childActive);
    },
    left(val) {
      const maxLength = this.$children.length - 1;
      if (val === 0) {
        this.childActive = maxLength;
      } else {
        this.childActive = this.childActive - 1;
      }

      this.activeChild(this.childActive);
    },
    changeTab(index) {
      this.expand = false;
      this.activeChild(index);
    },
    clickTag(child) {
      this.$emit('click-tag', child);
    },
    styleAlignIcon(icon) {
      return icon ? 'display:flex;align-items:center' : '';
    },
    parseIndex(index) {
      let activeIndex = this.childActive;
      if (index < 0) {
        activeIndex = 0;
      } else if (index >= this.$children.length) {
        activeIndex = this.$children.length - 1;
      } else if (typeof this.$children[index].$attrs.disabled === 'undefined') {
        activeIndex = parseInt(index);
      }
      return activeIndex;
    },
    activeChild(index, initialAnimation) {
      initialAnimation = !!initialAnimation;
      const elem = this.$refs.li[index];
      if (this.childActive == index && !initialAnimation) {
        this.these = true;
        elem.classList.add('isActive');
        setTimeout(() => {
          elem.classList.remove('isActive');
          this.these = false;
        }, 200);
      }
      this.$children.map((item, item_index) => {
        if (item_index != index) {
          item.active = false;
        }
      });
      if (this.childActive > index) {
        this.$children[index].invert = true;
        this.$children[this.childActive].invert = false;
      } else {
        this.$children[this.childActive].invert = true;
        this.$children[index].invert = false;
      }
      this.$children[index].active = true;
      this.childActive = index;
      this.$emit('input', this.childActive);
      if (this.position == 'left' || this.position == 'right') {
        this.$children[index].vertical = true;
      }
      this.changePositionLine(elem, initialAnimation);

      const buttonEl = document.getElementById(`tab-${this.uid}-${index}`);
      if (!initialAnimation && buttonEl) {
        buttonEl.focus();
      }
    },
    changePositionLine(elem, initialAnimation) {
      if (this.position == 'left' || this.position == 'right') {
        this.topx = elem.offsetTop;
        this.heightx = elem.offsetHeight;
        this.widthx = 2;
      } else {
        const update = () => {
          this.leftx = elem.offsetLeft;
          this.widthx = elem.offsetWidth;
          // this.topx = (elem.offsetHeight + (elem.getBoundingClientRect().top - this.$refs.ul.getBoundingClientRect().top))
        };
        if (!initialAnimation) {
          update();
        } else {
          setTimeout(update, 100);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown {
  border-radius: 6px;
  background: #a2d7bf;
  border: none;
  outline: none;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  width: 100%;
  user-select: none;
  box-shadow: 0 4px 8px rgba(83, 105, 97, 0.1);

  .header,
  ul li {
    padding: 10px 15px;
  }

  ul {
    border-top: 1px solid #ffe8e8;
  }

  ul li {
    cursor: pointer;
    background: #e4e8ed;
    color: #000;

    &.activeChild {
      background: #3f7d5f;
      color: #fff;
      pointer-events: none;

      &:hover {
        background: #3f7d5f;
      }
    }

    &:not(:last-child) {
      border-bottom: 1px solid #dadada;
    }

    &:hover {
      background: #a2d7bf;
    }
  }
}

button {
  margin: 0 5px;
  width: initial;

  border-color: transparent;

  justify-content: initial;
  border-bottom: 1px solid #e4e8ed;

  padding: 8px 20px;
  display: inline-block;
  background-color: #e4e8ed;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  color: #000;

  &:hover {
    color: #000 !important;
    background-color: rgba(162, 215, 191, 0.3);
  }

  // Current tab: 3.96:1 against the #e4e8ed of the other tabs and 4.87:1 against white
  // (WCAG SC 1.4.11, audit 21.2). White text on it is 4.87:1 (SC 1.4.3).
  &.activeChild {
    background-color: #3f7d5f;

    span {
      color: #fff !important;
    }
  }

  span {
    color: #000 !important;
  }
}

hr {
  border: 1px solid #e4e8ed;
}
</style>
